import { lazy, Suspense, type ComponentType, type ReactElement } from 'react'
import { createBrowserRouter, Navigate } from 'react-router-dom'

import FullPageSpinner from '@/components/ui/full-page-sinner'
import AuthenticatedLayout from '@/layouts/authenticated-layout'
import { ROUTES } from '@/config/routes'
import ErrorPage from '@/pages/error'

import Protected from './protected'
import AdminRoute from './admin'
import PermissionRoute from './permission'
import WebRoute from './webAcess'
import AcessRoute from './Acess'
import MinutaUserRoute from './minutaUserRoute'
import { AuthGate } from './AuthGate'
import ClientPortalRoute from './clientPortalRoute'

const SignIn = lazy(() => import('@/pages/SignIn'))
const ForgotPassword = lazy(() => import('@/pages/password-recovery'))
const ForgotPasswordClient = lazy(() => import('@/pages/password-recovery-client'))
const ResetPassword = lazy(() => import('@/pages/password-reset'))
const TermsOfUse = lazyNamed(() => import('@/components/form/termos'), 'TermsOfUse')
const PrivacyPolicy = lazyNamed(() => import('@/components/form/privacy-policy'), 'PrivacyPolicy')
const MinutaUser = lazy(() => import('@/components/form/agreement/minuta-user'))
const UpdateCepUsuario = lazy(() => import('@/pages/cep-usuario'))
const MinutaRead = lazy(() => import('@/components/form/agreement/minuta-read'))
const AssinarClient = lazy(() => import('@/features/signatures/components/assinar-client'))
const LoginFormClient = lazyNamed(() => import('@/features/auth/components'), 'LoginFormClient')
const LoginFormClientMinuta = lazyNamed(() => import('@/features/auth/components'), 'LoginFormClientMinuta')
const KlingerMobilePage = lazy(() => import('@/LandingPage/klingerLangingPage'))
const MDMobilePage = lazy(() => import('@/LandingPage/mdLangingPage'))
const BahiaSulPage = lazy(() => import('@/LandingPage/bahiaLandingPage'))
const TrisolutionPage = lazy(() => import('@/LandingPage/trisolutionLangingPage'))
const LandingPage = lazy(() => import('@/pages/leadingpage'))
const DSPage = lazy(() => import('@/LandingPage/dsLangingPage'))
const AlertsPage = lazyNamed(() => import('@/pages/alerts-page'), 'AlertsPage')
const ClientesListPage = lazyNamed(() => import('@/pages/clientes-list'), 'ClientesListPage')
const GerarMinutaPage = lazyNamed(() => import('@/pages/gerar-minuta'), 'GerarMinutaPage')
const CadastrarClientePage = lazyNamed(() => import('@/pages/cadastrar-cliente'), 'CadastrarClientePage')
const CadastrarClientePageCnpj = lazyNamed(() => import('@/pages/cadastrar-cliente-cnpj'), 'CadastrarClientePageCnpj')
const CadastrarClientePagePf = lazyNamed(() => import('@/pages/cadastrar-cliente-pf'), 'CadastrarClientePagePf')
const ListarParceirosPage = lazyNamed(() => import('@/pages/parceiros/parceiros-list'), 'ListarParceirosPage')
const CadastrarParceiroPage = lazyNamed(() => import('@/pages/parceiros/parceiros-cadastrar'), 'CadastrarParceiroPage')
const CadastrarContabilidadePage = lazyNamed(
  () => import('@/pages/parceiros/parceiros-cadastrar-contabilidade'),
  'CadastrarContabilidadePage'
)
const UsersPage = lazy(() => import('@/pages/admin/users'))
const ContratoForm = lazy(() => import('@/components/form/contrato/contrato-form'))
const BankRegistration = lazy(() => import('@/components/form/banco/bank-form'))
const ResumoBoleto = lazy(() => import('@/components/form/home/resumo-boleto'))
const FinanceiraResumo = lazy(() => import('@/components/form/home/resumo-financeiras'))
const SignaturesPage = lazy(() => import('@/pages/admin/signatures'))
const ClientesPage = lazy(() => import('@/pages/clients-page'))
const ClientsInfo = lazy(() => import('@/pages/clients/clients-info'))
const ClientsAddProduct = lazy(() => import('@/pages/clients/clients-add-product'))
const ClientsProducts = lazy(() => import('@/pages/clients/clients-products'))
const UserDetailsPage = lazy(() => import('@/pages/admin/user-details'))
const PermissionsManagerWrapper = lazyNamed(
  () => import('@/pages/admin/wrappers'),
  'PermissionsManagerWrapper'
)
const UserEditWrapper = lazyNamed(() => import('@/pages/admin/wrappers'), 'UserEditWrapper')
const ProductManagement = lazy(() => import('@/features/products/components/ProductManager'))
const EditPreview = lazy(() => import('@/features/agreement/components/minutaPreview'))
const ListarAgreements = lazy(() => import('@/features/agreement/components/lista-minuta-clients'))
const MinutaPage = lazy(() => import('@/features/agreement/components/minuta/index'))
const AssinarWitness = lazy(() => import('@/features/signatures/components/assinar-witness'))
const ClientHomePage = lazy(() => import('@/pages/client-home'))
const ConfirmOwnerEmailPage = lazy(() => import('@/pages/confirm-owner-email'))
const CriarEventoPage = lazy(() => import('@/pages/criar-evento'))

function lazyNamed<T extends ComponentType<any>, K extends string>(
  factory: () => Promise<Record<K, T>>,
  exportName: K
) {
  return lazy(async () => ({
    default: (await factory())[exportName]
  }))
}

function withSuspense(element: ReactElement) {
  return <Suspense fallback={<FullPageSpinner />}>{element}</Suspense>
}

const RouterList = createBrowserRouter(
  [
    {
    path: '/confirmar-email-proprietario',
    element: <WebRoute>{withSuspense(<ConfirmOwnerEmailPage />)}</WebRoute>,
    errorElement: <ErrorPage />
    },
    {
    path: ROUTES.FORGOT_PASSWORD.path,
    element: <WebRoute>{withSuspense(<ForgotPassword />)}</WebRoute>,
    errorElement: <ErrorPage />
    },
    {
    path: ROUTES.FORGOT_PASSWORD_CLIENT.path,
    element: <WebRoute>{withSuspense(<ForgotPasswordClient />)}</WebRoute>,
    errorElement: <ErrorPage />
    },
    {
    path: ROUTES.RESET_PASSWORD.path,
    element: <WebRoute>{withSuspense(<ResetPassword />)}</WebRoute>,
    errorElement: <ErrorPage />
    },
  { path: ROUTES.MD_MOBILE.path, element: withSuspense(<MDMobilePage />), errorElement: <ErrorPage /> },
  { path: ROUTES.KLINGER_MOBILE.path, element: withSuspense(<KlingerMobilePage />), errorElement: <ErrorPage /> },
  { path: ROUTES.BAHIA_SUL.path, element: withSuspense(<BahiaSulPage />), errorElement: <ErrorPage /> },
  { path: ROUTES.TRISOLUTION.path, element: withSuspense(<TrisolutionPage />), errorElement: <ErrorPage /> },
  { path: ROUTES.DS_PAGE.path, element: withSuspense(<DSPage />), errorElement: <ErrorPage /> },
  {
    path: '/minuta/:id',
    element: <WebRoute>{withSuspense(<MinutaPage />)}</WebRoute>,
    errorElement: <ErrorPage />
  },
  {
    path: '/assinar-testemunha/:id_client/:slot',
    element: <WebRoute>{withSuspense(<AssinarWitness />)}</WebRoute>,
    errorElement: <ErrorPage />
  },
  {
    path: '/minuta-read/:idtenant/:contratos',
    element: <WebRoute>{withSuspense(<MinutaRead />)}</WebRoute>,
    errorElement: <ErrorPage />
  },
  {
    path: ROUTES.LOGIN_CLIENT_MINUTA.path,
    element: <WebRoute>{withSuspense(<LoginFormClientMinuta />)}</WebRoute>,
    errorElement: <ErrorPage />
  },
  {
    path: ROUTES.LOGIN_CLIENT.path,
    element: <WebRoute>{withSuspense(<LoginFormClient />)}</WebRoute>,
    errorElement: <ErrorPage />
  },
  {
    path: ROUTES.CLIENT_HOME.path,
    element: (
      <WebRoute>
        <ClientPortalRoute>{withSuspense(<ClientHomePage />)}</ClientPortalRoute>
      </WebRoute>
    ),
    errorElement: <ErrorPage />
  },
  {
    path: '/client-home/minuta/:id',
    element: (
      <WebRoute>
        <ClientPortalRoute>{withSuspense(<MinutaPage />)}</ClientPortalRoute>
      </WebRoute>
    ),
    errorElement: <ErrorPage />
  },
  {
    path: '/assinar-contrato/:id_client',
    element: (
      <WebRoute>
        <ClientPortalRoute>{withSuspense(<AssinarClient />)}</ClientPortalRoute>
      </WebRoute>
    ),
    errorElement: <ErrorPage />
  },
  {
    path: ROUTES.SIGNIN.path,
    element: <WebRoute>{withSuspense(<SignIn />)}</WebRoute>,
    errorElement: <ErrorPage />
  },
  {
    path: ROUTES.CEP.path,
    element: <WebRoute>{withSuspense(<UpdateCepUsuario />)}</WebRoute>,
    errorElement: <ErrorPage />
  },
  {
    path: ROUTES.TERMS_OF_USE.path,
    element: <WebRoute>{withSuspense(<TermsOfUse />)}</WebRoute>,
    errorElement: <ErrorPage />
  },
  {
    path: ROUTES.PRIVACY_POLICY.path,
    element: <WebRoute>{withSuspense(<PrivacyPolicy />)}</WebRoute>,
    errorElement: <ErrorPage />
  },
  {
    path: ROUTES.CREATE_EVENT.path,
    element: <WebRoute>{withSuspense(<CriarEventoPage />)}</WebRoute>,
    errorElement: <ErrorPage />
  },
  {
    path: ROUTES.HOME.path,
    element: <AcessRoute>{withSuspense(<LandingPage />)}</AcessRoute>,
    errorElement: <ErrorPage />
  },
  {
    path: '/',
    element: <AuthGate />,
    children: [
      {
        path: '/minuta-usuario/:id',
        element: (
          <WebRoute>
            <Protected>{withSuspense(<MinutaUser />)}</Protected>
          </WebRoute>
        ),
        errorElement: <ErrorPage />
      },
      {
        path: ROUTES.MINUTE_USER.path,
        element: (
          <WebRoute>
            <MinutaUserRoute>{withSuspense(<MinutaUser />)}</MinutaUserRoute>
          </WebRoute>
        ),
        errorElement: <ErrorPage />
      },
      {
        path: '/edit-preview/:editId',
        element: <WebRoute>{withSuspense(<EditPreview />)}</WebRoute>,
        errorElement: <ErrorPage />
      },
      {
        path: ROUTES.DASHBOARD_HOME.path,
        element: (
          <WebRoute>
            <AuthenticatedLayout>{withSuspense(<AlertsPage />)}</AuthenticatedLayout>
          </WebRoute>
        ),
        errorElement: <ErrorPage />
      },
      {
        path: ROUTES.ALERTS.path,
        element: (
          <WebRoute>
            <AuthenticatedLayout>{withSuspense(<AlertsPage />)}</AuthenticatedLayout>
          </WebRoute>
        ),
        errorElement: <ErrorPage />
      },
      {
        path: ROUTES.CLIENTS.path,
        element: (
          <WebRoute>
            <AuthenticatedLayout>{withSuspense(<ClientesListPage />)}</AuthenticatedLayout>
          </WebRoute>
        ),
        errorElement: <ErrorPage />
      },
      {
        path: ROUTES.GENERATE_MINUTE.path,
        element: (
          <WebRoute>
            <AuthenticatedLayout>{withSuspense(<GerarMinutaPage />)}</AuthenticatedLayout>
          </WebRoute>
        ),
        errorElement: <ErrorPage />
      },
      {
        path: ROUTES.REGISTER_CLIENT.path,
        element: (
          <WebRoute>
            <AuthenticatedLayout>{withSuspense(<CadastrarClientePage />)}</AuthenticatedLayout>
          </WebRoute>
        ),
        errorElement: <ErrorPage />
      },
      {
        path: ROUTES.REGISTER_CLIENT_CNPJ.path,
        element: (
          <WebRoute>
            <AuthenticatedLayout>{withSuspense(<CadastrarClientePageCnpj />)}</AuthenticatedLayout>
          </WebRoute>
        ),
        errorElement: <ErrorPage />
      },
      {
        path: ROUTES.REGISTER_CLIENT_PF.path,
        element: (
          <WebRoute>
            <AuthenticatedLayout>{withSuspense(<CadastrarClientePagePf />)}</AuthenticatedLayout>
          </WebRoute>
        ),
        errorElement: <ErrorPage />
      },
      {
        path: ROUTES.PARTNERS.path,
        element: (
          <WebRoute>
            <AuthenticatedLayout>{withSuspense(<ListarParceirosPage />)}</AuthenticatedLayout>
          </WebRoute>
        ),
        errorElement: <ErrorPage />
      },
      {
        path: ROUTES.REGISTER_PARTNER.path,
        element: (
          <WebRoute>
            <AuthenticatedLayout>{withSuspense(<CadastrarParceiroPage />)}</AuthenticatedLayout>
          </WebRoute>
        ),
        errorElement: <ErrorPage />
      },
      {
        path: ROUTES.REGISTER_ACCOUNTING.path,
        element: (
          <WebRoute>
            <AuthenticatedLayout>{withSuspense(<CadastrarContabilidadePage />)}</AuthenticatedLayout>
          </WebRoute>
        ),
        errorElement: <ErrorPage />
      },
      {
        path: 'admin',
        children: [
          {
            path: 'home',
            element: <Navigate to={ROUTES.ALERTS.path} replace />
          },
          {
            path: ':cnpjcpf/clientes',
            element: (
              <WebRoute>
                <AuthenticatedLayout>{withSuspense(<ClientesListPage />)}</AuthenticatedLayout>
              </WebRoute>
            ),
            errorElement: <ErrorPage />
          },
          {
            path: ':tenant/clientes/:cnpjcpf',
            element: (
              <WebRoute>
                <AuthenticatedLayout>
                  <PermissionRoute permissionType="clients">{withSuspense(<ClientesPage />)}</PermissionRoute>
                </AuthenticatedLayout>
              </WebRoute>
            ),
            errorElement: <ErrorPage />
          },
          {
            path: ':tenant/clientes/:cnpjcpf/informacoes',
            element: (
              <WebRoute>
                <AuthenticatedLayout>
                  <PermissionRoute permissionType="clients">{withSuspense(<ClientsInfo />)}</PermissionRoute>
                </AuthenticatedLayout>
              </WebRoute>
            ),
            errorElement: <ErrorPage />
          },
          {
            path: ':tenant/clientes/:cnpjcpf/adicionar-produto',
            element: (
              <WebRoute>
                <AuthenticatedLayout>
                  <PermissionRoute permissionType="clients">{withSuspense(<ClientsAddProduct />)}</PermissionRoute>
                </AuthenticatedLayout>
              </WebRoute>
            ),
            errorElement: <ErrorPage />
          },
          {
            path: ':tenant/clientes/:cnpjcpf/lista-minutas',
            element: (
              <WebRoute>
                <AuthenticatedLayout>{withSuspense(<ListarAgreements />)}</AuthenticatedLayout>
              </WebRoute>
            ),
            errorElement: <ErrorPage />
          },
          {
            path: ':tenant/clientes/:cnpjcpf/produtos',
            element: (
              <WebRoute>
                <AuthenticatedLayout>
                  <PermissionRoute permissionType="clients">{withSuspense(<ClientsProducts />)}</PermissionRoute>
                </AuthenticatedLayout>
              </WebRoute>
            ),
            errorElement: <ErrorPage />
          },
          {
            path: ':cnpjcpf/opcoes-financeiras',
            element: (
              <WebRoute>
                <AuthenticatedLayout>{withSuspense(<FinanceiraResumo />)}</AuthenticatedLayout>
              </WebRoute>
            ),
            errorElement: <ErrorPage />
          },
          {
            path: ':cnpjcpf/opcoes-fiscais',
            element: (
              <WebRoute>
                <AuthenticatedLayout>{withSuspense(<ResumoBoleto />)}</AuthenticatedLayout>
              </WebRoute>
            ),
            errorElement: <ErrorPage />
          },
          {
            path: 'usuarios',
            element: (
              <WebRoute>
                <AdminRoute>{withSuspense(<UsersPage />)}</AdminRoute>
              </WebRoute>
            ),
            errorElement: <ErrorPage />
          },
          {
            path: 'usuarios/:id',
            element: (
              <WebRoute>
                <AdminRoute>{withSuspense(<UserDetailsPage />)}</AdminRoute>
              </WebRoute>
            ),
            errorElement: <ErrorPage />,
            children: [
              { index: true, element: <Navigate to="./permissoes" replace /> },
              { path: 'permissoes', element: withSuspense(<PermissionsManagerWrapper />) },
              { path: 'editar', element: withSuspense(<UserEditWrapper />) }
            ]
          },
          {
            path: 'product-analista',
            element: (
              <WebRoute>
                <AdminRoute>{withSuspense(<ProductManagement />)}</AdminRoute>
              </WebRoute>
            ),
            errorElement: <ErrorPage />
          },
          {
            path: 'contratos',
            element: (
              <WebRoute>
                <AdminRoute>{withSuspense(<ContratoForm />)}</AdminRoute>
              </WebRoute>
            ),
            errorElement: <ErrorPage />
          },
          {
            path: 'info-banco',
            element: (
              <WebRoute>
                <AdminRoute>{withSuspense(<BankRegistration />)}</AdminRoute>
              </WebRoute>
            ),
            errorElement: <ErrorPage />
          }
        ]
      },
      {
        path: ROUTES.AGREEMENT_SIGNATURES.path,
        element: (
          <WebRoute>
            <AuthenticatedLayout>{withSuspense(<SignaturesPage />)}</AuthenticatedLayout>
          </WebRoute>
        ),
        errorElement: <ErrorPage />
      },
      {
        path: 'cliente/:cnpjcpf',
        element: (
          <WebRoute>
            <Protected>
              <PermissionRoute permissionType="clients">
                <AuthenticatedLayout>{withSuspense(<ClientesPage />)}</AuthenticatedLayout>
              </PermissionRoute>
            </Protected>
          </WebRoute>
        ),
        errorElement: <ErrorPage />
      },
      {
        path: 'cliente/:cnpjcpf/informacoes',
        element: (
          <WebRoute>
            <AuthenticatedLayout>
              <PermissionRoute permissionType="clients">{withSuspense(<ClientsInfo />)}</PermissionRoute>
            </AuthenticatedLayout>
          </WebRoute>
        ),
        errorElement: <ErrorPage />
      },
      {
        path: 'cliente/:cnpjcpf/adicionar-produto',
        element: (
          <WebRoute>
            <AuthenticatedLayout>
              <PermissionRoute permissionType="clients">{withSuspense(<ClientsAddProduct />)}</PermissionRoute>
            </AuthenticatedLayout>
          </WebRoute>
        ),
        errorElement: <ErrorPage />
      },
      {
        path: 'cliente/:cnpjcpf/lista-minutas',
        element: (
          <WebRoute>
            <AuthenticatedLayout>{withSuspense(<ListarAgreements />)}</AuthenticatedLayout>
          </WebRoute>
        ),
        errorElement: <ErrorPage />
      },
      {
        path: 'cliente/:cnpjcpf/produtos',
        element: (
          <WebRoute>
            <AuthenticatedLayout>
              <PermissionRoute permissionType="clients">{withSuspense(<ClientsProducts />)}</PermissionRoute>
            </AuthenticatedLayout>
          </WebRoute>
        ),
        errorElement: <ErrorPage />
      }
    ]
  },
  {
    path: ROUTES.NOT_FOUND.path,
    element: <WebRoute>{withSuspense(<SignIn />)}</WebRoute>,
    errorElement: <ErrorPage />
  }
])

export default RouterList
